if (!customElements.get('delivery-countdown')) {
  customElements.define(
    'delivery-countdown',
    class DeliveryCountdown extends HTMLElement {
      static WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

      connectedCallback() {
        this.valueElement = this.querySelector('[data-countdown-value]');
        this.dateElement = this.querySelector('[data-countdown-date]');
        if (!this.valueElement && !this.dateElement) return;

        this.timezone = this.dataset.timezone || 'UTC';
        this.locale = this.dataset.locale || 'en';
        this.cutoffWeekday = this.clampWeekday(this.dataset.cutoffWeekday, 0);
        this.cutoffTime = this.parseTimeOfDay(this.dataset.cutoffTime);
        this.deliveryWeekday = this.clampWeekday(this.dataset.deliveryWeekday, 5);
        this.deliveryOffset = this.getDeliveryOffset();
        this.dateFormat = this.dataset.dateFormat || 'numeric';
        this.showSeconds = this.dataset.showSeconds !== 'false';
        this.units = (this.dataset.units || 'd,t,m,s').split(',');

        this.render();
        this.intervalId = setInterval(this.render.bind(this), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.intervalId);
      }

      clampWeekday(value, fallback) {
        const weekday = Number(value);
        return Number.isInteger(weekday) && weekday >= 0 && weekday <= 6 ? weekday : fallback;
      }

      parseTimeOfDay(value) {
        const match = /^(\d{1,2}):(\d{2})$/.exec((value || '').trim());
        if (!match) return { hour: 23, minute: 59 };

        return {
          hour: Math.min(23, Math.max(0, Number(match[1]))),
          minute: Math.min(59, Math.max(0, Number(match[2]))),
        };
      }

      // Days from the cut-off day to the delivery day. The first matching weekday
      // strictly after the cut-off, minus a week when the delivery day belongs to
      // the cut-off week itself.
      getDeliveryOffset() {
        const daysAfter = (((this.deliveryWeekday - this.cutoffWeekday + 6) % 7) + 1);
        return this.dataset.deliveryTiming === 'after_cutoff' ? daysAfter : daysAfter - 7;
      }

      // Wall-clock time in this.timezone for a given instant.
      zonedParts(date) {
        const raw = {};
        new Intl.DateTimeFormat('en-US', {
          timeZone: this.timezone,
          hourCycle: 'h23',
          weekday: 'short',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
          .formatToParts(date)
          .forEach((part) => {
            if (part.type !== 'literal') raw[part.type] = part.value;
          });

        return {
          year: Number(raw.year),
          month: Number(raw.month),
          day: Number(raw.day),
          hour: Number(raw.hour),
          minute: Number(raw.minute),
          second: Number(raw.second),
          weekday: DeliveryCountdown.WEEKDAY_INDEX[raw.weekday],
        };
      }

      // Wall-clock time in this.timezone -> UTC timestamp. Stays correct across DST
      // because the offset is measured at the target instant rather than assumed.
      zonedToTimestamp(year, month, day, hour, minute) {
        const guess = Date.UTC(year, month - 1, day, hour, minute, 0);
        const parts = this.zonedParts(new Date(guess));
        const asUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
        return guess - (asUTC - guess);
      }

      cutoffFor(dayMarker) {
        return this.zonedToTimestamp(
          dayMarker.getUTCFullYear(),
          dayMarker.getUTCMonth() + 1,
          dayMarker.getUTCDate(),
          this.cutoffTime.hour,
          this.cutoffTime.minute
        );
      }

      getCutoff(nowMs) {
        const today = this.zonedParts(new Date(nowMs));
        // Day markers are held at UTC midnight so date maths never crosses a DST boundary.
        const cutoffDay = new Date(Date.UTC(today.year, today.month - 1, today.day));
        cutoffDay.setUTCDate(cutoffDay.getUTCDate() + ((this.cutoffWeekday - today.weekday + 7) % 7));

        let timestamp = this.cutoffFor(cutoffDay);
        if (timestamp <= nowMs) {
          // This week's cut-off has passed — roll on to the next one.
          cutoffDay.setUTCDate(cutoffDay.getUTCDate() + 7);
          timestamp = this.cutoffFor(cutoffDay);
        }

        const deliveryDay = new Date(cutoffDay.getTime());
        deliveryDay.setUTCDate(deliveryDay.getUTCDate() + this.deliveryOffset);

        return { timestamp, deliveryDay };
      }

      formatRemaining(ms) {
        const totalSeconds = Math.max(0, Math.floor(ms / 1000));
        const [dayUnit, hourUnit, minuteUnit, secondUnit] = this.units;
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        let remaining = days > 0 ? `${days}${dayUnit} ` : '';
        remaining += `${hours}${hourUnit} ${minutes}${minuteUnit}`;
        if (this.showSeconds) remaining += ` ${totalSeconds % 60}${secondUnit}`;

        return remaining;
      }

      formatDate(dayMarker) {
        if (this.dateFormat === 'long') {
          return new Intl.DateTimeFormat(this.locale, {
            timeZone: 'UTC',
            day: 'numeric',
            month: 'long',
          }).format(dayMarker);
        }

        return `${dayMarker.getUTCDate()}/${dayMarker.getUTCMonth() + 1}/${dayMarker.getUTCFullYear()}`;
      }

      render() {
        const now = Date.now();
        const { timestamp, deliveryDay } = this.getCutoff(now);

        if (this.valueElement) this.valueElement.textContent = this.formatRemaining(timestamp - now);
        if (this.dateElement) this.dateElement.textContent = this.formatDate(deliveryDay);
      }
    }
  );
}
